import React, { useState } from "react";
import { Skeleton } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import {
  HiOutlineInformationCircle,
  HiOutlineChevronRight,
  HiOutlineChevronDown,
  HiTrendingUp,
  HiOutlineRefresh,
} from "react-icons/hi";
import { Info } from "lucide-react";
import CountUp from "react-countup";
import ContactPagination from "../../../components/Pagination/ContactPagination";
import DataAsOfNotice from "../../../components/DataAsOfNotice";
import axios from "axios";
import { useEffect } from "react";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import exportIcon from "../../../assets/icons/upload.svg";
import ExportModal from "../../../components/ImpExpModal/ExportModal";
import Loader from "../../../components/Loader";

const TransactionDashboardNew = ({
  isSidebarCollapsed,
  onCallApi,
  exportCsvData,
  selectedData,
  advanceFilterData = [],
  activeTab,
  from,
  viewAs,
}) => {
  const [config] = useAuth();
  const userDetails = useSelector((state) => state.userDetails);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [exportType, setExportType] = useState("deal");
  const [expandedRow, setExpandedRow] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const activeToggle = viewAs === "production" ? 2 : 1;

  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });

  const handleExport = (type) => {
    setExportType(type);
    setShowModal(true);
  };

  const buildQueryParams = () => {
    let params = `per_page=${itemPerPage}&page=${currentPage}`;
    return params;
  };

  const fetchDashboardData = () => {
    setLoading(true);
    const queryString = buildQueryParams();

    // We filter advance filters here as done in ContractToClosed
    const validAdvanceFilters = (advanceFilterData || []).filter((group) =>
      group?.filters?.some((f) => f.field && f.operator),
    );
    const advanceFiltersQuery = encodeURIComponent(
      JSON.stringify(validAdvanceFilters),
    );

    axios
      .get(
        `${BASE_URL}/transaction-dashboard?${queryString}&advance_filters=${advanceFiltersQuery}&active_tab=${activeTab}&active_toggle=${activeToggle}`,
        config,
      )
      .then((res) => {
        const data = res?.data;
        setDashboardData(data);
        setPaginationData({
          totalItems: data?.total ?? 0,
          from: data?.from ?? 0,
          to: data?.to ?? 0,
          totalPages: data?.last_page ?? 0,
        });
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  const renderAnimatedCount = (val, prefix = "", suffix = "") => {
    if (val == null || val === "") return val;
    const num = Number(String(val).replace(/,/g, ''));
    return !isNaN(num) ? <CountUp end={num} separator="," duration={2} prefix={prefix} suffix={suffix} /> : `${prefix}${val}${suffix}`;
  };

  useEffect(() => {
    fetchDashboardData();
  }, [currentPage, itemPerPage, advanceFilterData, activeTab, viewAs]);

  useEffect(() => {
    setCurrentPage(1);
  }, [advanceFilterData, activeTab, viewAs]);

  const handleNext = () => {
    if (currentPage < paginationData.totalPages)
      setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getStatusColorClass = (color) => {
    switch (color) {
      case "blue":
        return "bg-blue-100 text-blue-700";
      case "orange":
        return "bg-orange-100 text-orange-700";
      case "green":
        return "bg-green-100 text-green-700";
      case "grey":
        return "bg-gray-100 text-gray-700";
      case "purple":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };


  const goalData =
    dashboardData?.transaction_dashboard?.annual_goal_progress || {};
  const parseAmt = (val) =>
    parseFloat((val || "0").toString().replace(/[^0-9.-]+/g, "")) || 0;
  const goalPercentageText =
    goalData.percentage !== undefined ? Math.round(goalData.percentage) : 0;
  const goalPercentageVisual = Math.min(100, goalPercentageText);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);

  return (
    <div className="light-bg-L min-h-screen p-6 body-N dark-H relative min-h-[400px]">
      {loading && (
        <div className="absolute inset-0 z-20 flex justify-center items-start pt-[20vh] bg-white/40 backdrop-blur-[1px] rounded-lg">
          <Loader />
        </div>
      )}
      {/* TOP SECTION: SUMMARY CARDS */}
      <div className="flex flex-col md:flex-row flex-wrap xl:flex-nowrap gap-4 xl:gap-6 mb-6 w-full">
        {/* Card 1 */}
        <div className="flex-1 min-w-[200px] bg-white rounded-lg shadow-sm p-4 xl:p-5 border border-gray-100 overflow-hidden">
          <div className="flex items-start text-[10px] xl:text-xs font-semibold text-gray-500 uppercase tracking-wide  min-h-[32px] xl:min-h-[40px]">
            <span className="flex-1">
              {activeToggle === 2 ? "My LOI Pipeline" : "Company LOI Pipeline"}
            </span>{" "}
            <HiOutlineInformationCircle className="ml-1 text-base shrink-0 mt-0.5 xl:mt-0" />
          </div>
          <div className="text-xl xl:text-2xl font-bold text-green-600 mb-2 whitespace-nowrap truncate">
            {renderAnimatedCount(dashboardData?.transaction_dashboard?.current_loi_pipeline?.gci || "0", "$")}
          </div>
          <div className="text-sm text-gray-500 font-medium">
            {renderAnimatedCount(dashboardData?.transaction_dashboard?.current_loi_pipeline?.count || 0)} deals
          </div>
        </div>

        {/* Card 2 */}
        <div className="flex-1 min-w-[200px] bg-white rounded-lg shadow-sm p-4 xl:p-5 border border-gray-100 overflow-hidden">
          <div className="flex items-start text-[10px] xl:text-xs font-semibold text-gray-500 uppercase tracking-wide  min-h-[32px] xl:min-h-[40px]">
            <span className="flex-1">
              {activeToggle === 2
                ? "My Hopper (Under Contract)"
                : "Company Hopper (Under Contract)"}
            </span>{" "}
            <HiOutlineInformationCircle className="ml-1 text-base shrink-0 mt-0.5 xl:mt-0" />
          </div>
          <div className="text-xl xl:text-2xl font-bold text-blue-600 mb-2 whitespace-nowrap truncate">
            {renderAnimatedCount(dashboardData?.transaction_dashboard?.current_hopper?.gci || "0", "$")}
          </div>
          <div className="text-sm text-gray-500 font-medium">
            {renderAnimatedCount(dashboardData?.transaction_dashboard?.current_hopper?.count || 0)} deals
          </div>
        </div>

        {/* Card 3 */}
        <div className="flex-1 min-w-[200px] bg-white rounded-lg shadow-sm p-4 xl:p-5 border border-gray-100 overflow-hidden">
          <div className="flex items-start text-[10px] xl:text-xs font-semibold text-gray-500 uppercase tracking-wide  min-h-[32px] xl:min-h-[40px]">
            <span className="flex-1">
              {activeToggle === 2 ? "My Closed YTD" : "Company Closed YTD"}
            </span>
            <HiOutlineInformationCircle className="ml-1 text-base shrink-0 mt-0.5 xl:mt-0" />
          </div>
          <div className="text-xl xl:text-2xl font-bold text-purple-700 mb-2 whitespace-nowrap truncate">
            {renderAnimatedCount(dashboardData?.transaction_dashboard?.closed_ytd?.gci || "0", "$")}
          </div>
          <div className="text-sm text-gray-500 font-medium whitespace-nowrap truncate">
            {renderAnimatedCount(dashboardData?.transaction_dashboard?.closed_ytd?.count || 0)} deals
          </div>
        </div>

        {/* Card 4 (Goal Progress) */}
        <div
          style={{ flex: 1.3 }}
          className="min-w-[250px] bg-white rounded-lg shadow-sm p-4 xl:p-5 border border-gray-100 overflow-hidden"
        >
          <div className="flex items-start text-[10px] xl:text-xs font-semibold text-gray-500 uppercase tracking-wide  min-h-[32px] xl:min-h-[40px]">
            <span className="flex-1">
              {activeToggle === 2
                ? "Annual Goal Progress"
                : "Company Goal Progress"}
            </span>
            <HiOutlineInformationCircle className="ml-1 text-base shrink-0 mt-0.5 xl:mt-0" />
          </div>
          <div className="flex items-center gap-2 xl:gap-4">
            {/* Donut Chart placeholder */}
            <div className="relative w-16 h-16 xl:w-20 xl:h-20 flex-shrink-0">
              <svg
                className="w-full h-full transform -rotate-90"
                viewBox="0 0 36 36"
              >
                <path
                  className="text-gray-100"
                  strokeWidth="3.8"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-green-700"
                  strokeWidth="3.8"
                  strokeDasharray={`${goalPercentageVisual}, 100`}
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[14px] xl:text-lg font-bold text-gray-800 leading-none">
                  {renderAnimatedCount(goalPercentageText)}%
                </span>
                <span className="text-[8px] xl:text-[10px] text-gray-500 font-medium">
                  of goal
                </span>
              </div>
            </div>
            {/* Goal Breakdown */}
            <div className="flex-1 text-[9px] xl:text-[11px] font-medium text-gray-500 space-y-1.5 whitespace-nowrap overflow-hidden">
              <div className="flex justify-between items-center text-gray-800 gap-1">
                <span
                  className="whitespace-normal leading-tight break-words"
                  style={{ wordBreak: "break-word" }}
                >
                  {activeToggle === 2
                    ? "Annual Goal"
                    : "Annual Company GCI Goal"}
                </span>
                <span className="font-semibold whitespace-nowrap shrink-0">
                  {renderAnimatedCount(goalData.annual_goal || "0", "$")}
                </span>
              </div>
              <div className="flex justify-between gap-1">
                <span>Closed YTD</span>
                <span className="font-semibold text-gray-800 shrink-0">
                  {renderAnimatedCount(goalData.closed_ytd || "0", "$")}
                </span>
              </div>
              <div className="flex justify-between gap-1">
                <span>Under Contract</span>
                <span className="font-semibold text-gray-800 shrink-0">
                  {renderAnimatedCount(goalData.under_contract || "0", "$")}
                </span>
              </div>
              <div className="flex justify-between gap-1">
                <span>LOI Pipeline</span>
                <span className="font-semibold text-gray-800 shrink-0">
                  {renderAnimatedCount(goalData.loi_pipeline || "0", "$")}
                </span>
              </div>
              <div className="border-t border-gray-100 pt-1.5 mt-1.5 flex justify-between gap-1">
                <span className="font-semibold text-gray-800">Remaining</span>
                <span className="font-semibold text-green-600 shrink-0">
                  {renderAnimatedCount(goalData.remaining || "0", "$")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MIDDLE SECTION: FORECASTED COMMISSIONS */}
      <div className="bg-white rounded-lg shadow-sm p-4 xl:p-5 mb-6 border border-gray-100 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6">
        <div className="flex-1 w-full overflow-x-auto pb-2 xl:pb-0">
          <div className="flex items-center text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4 whitespace-nowrap">
            {activeToggle === 2
              ? "Forecasted Commission (My Commission)"
              : "Forecasted Company Gross Commissions"}{" "}
            <HiOutlineInformationCircle className="ml-1 text-lg" />
          </div>
          <div className="flex items-center justify-between xl:justify-start xl:gap-24 w-full xl:w-4/5 min-w-max">
            <div>
              <div className="text-[10px] uppercase font-bold text-gray-400 mb-1">
                30 Days
              </div>
              <div className="text-2xl font-bold text-green-600">
                {renderAnimatedCount(dashboardData?.transaction_dashboard?.expected_closings?.["30_days"]?.gci || "0", "$")}
              </div>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div>
              <div className="text-[10px] uppercase font-bold text-gray-400 mb-1">
                60 Days
              </div>
              <div className="text-2xl font-bold text-green-600">
                {renderAnimatedCount(dashboardData?.transaction_dashboard?.expected_closings?.["60_days"]?.gci || "0", "$")}
              </div>
            </div>
            <div className="w-px h-12 bg-gray-200"></div>
            <div>
              <div className="text-[10px] uppercase font-bold text-gray-400 mb-1">
                90 Days
              </div>
              <div className="text-2xl font-bold text-green-600">
                {renderAnimatedCount(dashboardData?.transaction_dashboard?.expected_closings?.["90_days"]?.gci || "0", "$")}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-[#f2f9f4] rounded-lg p-4 flex items-start gap-3 border border-green-100 w-full xl:w-[400px] shrink-0">
          <HiTrendingUp className="text-green-600 text-2xl mt-0.5 shrink-0" />
          <div>
            <div className="text-md font-bold text-green-600 mb-1">On Pace</div>
            <div className="text-[12px] text-gray-500 leading-relaxed">
              You are on track to hit your annual goal!
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM SECTION: DEALS TABLE */}
      <div className="light-bg-L pb-3 table-container mt-4 w-full">
        {/* Title and Export Header (ABOVE TABLE) */}
        <div className="flex justify-between items-center mb-4">
          <p className="head-4 dark-H">
            Deals{" "}
            <span className="text-gray-500">
              ({dashboardData?.deal?.length || 0})
            </span>
          </p>
          <div
            className="flex items-center cursor-pointer"
            onClick={() => handleExport("deal")}
          >
            <img src={exportIcon} alt="Export" className="mr-2" />
            <p className="text-green-H body-S hover:opacity-80">Export</p>
          </div>
        </div>

        {/* Table Container */}
        <div
          className="w-full bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden"
          style={{ height: "calc(100vh - 220px)" }}
        >
          <div className="overflow-auto flex-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 deals-table-wrapper">
            <table className="contact-table two-rows-static light-bg-L w-full min-w-full text-left whitespace-nowrap bg-white border-0">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="uppercase body-N dark-M text-left bg-[#d1dbcf]">
                  <th className="p-3 w-10"></th>
                  <th className="p-4">Property</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Advisor</th>
                  <th className="p-4">
                    Acquisition
                    <br />
                    (Contact)
                  </th>
                  <th className="p-4">
                    Disposition
                    <br />
                    (Contact)
                  </th>
                  <th className="p-4">NOI</th>
                  <th className="p-4">
                    Contract
                    <br />
                    Cap Rate
                  </th>
                  <th className="p-4">
                    Contract
                    <br />
                    Price
                  </th>
                  <th className="p-4">
                    Company Gross
                    <br />
                    Commission (GCI)
                  </th>
                  <th className="p-4">
                    <div className="flex items-center gap-1">
                      <span>
                        {activeToggle === 2 ? "My" : "Advisor"}
                        <br />
                        Commission
                      </span>{" "}
                      {/* <HiOutlineInformationCircle className="text-[14px]" /> */}
                    </div>
                  </th>
                  <th className="p-4">
                    Company
                    <br />
                    Commission
                  </th>
                  <th className="p-4">
                    Executed
                    <br />
                    LOI Date
                  </th>
                  <th className="p-4">
                    Contract
                    <br />
                    Date
                  </th>
                  <th className="p-4">
                    Expected
                    <br />
                    Close Date
                  </th>
                  <th className="p-4">
                    Closed
                    <br />
                    Date
                  </th>
                  <th className="p-4">
                    Lost
                    <br />
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="body-N dark-M divide-y">
                {loading ? (
                  [...Array(Number(itemPerPage) || 10)].map((_, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="p-3 w-10"><Skeleton height="20px" width="20px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="180px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="100px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="120px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="120px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="120px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="100px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="100px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="120px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="120px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="120px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="120px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="100px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="100px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="100px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="100px" /></td>
                      <td className="p-4"><Skeleton height="20px" width="100px" /></td>
                    </tr>
                  ))
                ) : (!dashboardData?.deal || dashboardData.deal.length === 0) ? (
                  <tr>
                    <td colSpan="17" className="p-8 text-center text-gray-500 font-medium">
                      No data available
                    </td>
                  </tr>
                ) : (
                  (dashboardData?.deal || []).map((deal) => {
                  const gciNumeric =
                    parseFloat(
                      deal?.gci_to_company?.replace(/[^0-9.-]+/g, ""),
                    ) || 0;
                  const companyCommissionHalf = gciNumeric / 2;
                  const formattedHalf = new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(companyCommissionHalf);

                  let formattedTotalAdvisorCommission = "-";
                  if (deal?.advisors?.length > 0) {
                    const totalAdvisorCommission = deal.advisors.reduce(
                      (acc, adv) => {
                        const val =
                          parseFloat(
                            (adv.commission_format || "0").replace(
                              /[^0-9.-]+/g,
                              "",
                            ),
                          ) || 0;
                        return acc + val;
                      },
                      0,
                    );
                    formattedTotalAdvisorCommission = new Intl.NumberFormat(
                      "en-US",
                      {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    ).format(totalAdvisorCommission);
                  }

                  return (
                    <React.Fragment key={deal.id}>
                      <tr
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${expandedRow === deal.id ? "bg-gray-50" : ""}`}
                        onClick={() => toggleRow(deal.id)}
                      >
                        <td className="p-4 text-gray-400 text-center">
                          {expandedRow === deal.id ? (
                            <HiOutlineChevronDown />
                          ) : (
                            <HiOutlineChevronRight />
                          )}
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="dark-H head-5">
                              {deal?.property?.name || "-"}
                            </p>
                            {deal?.property?.address ? (
                              <>
                                <p className="text-gray-500 body-N">
                                  {deal.property.address.street_address}
                                </p>
                                <p className="text-gray-500 body-N">
                                  {deal.property.address.city},{" "}
                                  {deal.property.address.state}
                                </p>
                              </>
                            ) : (
                              <p className="text-gray-500 body-N">-</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${getStatusColorClass(deal?.status_color)}`}
                          >
                            {deal?.status || "-"}
                          </span>
                        </td>
                        <td className="p-4 whitespace-nowrap">
                          {deal?.advisors?.length > 0
                            ? deal.advisors.map((adv, idx) => (
                                <div
                                  key={idx}
                                  className={idx > 0 ? "mt-1" : ""}
                                >
                                  {adv.name} (<span className="capitalize">
                                  {adv.type ? adv.type.toLowerCase().replace(/_?broker/gi, "").trim() : "-"}
                                  </span>)
                                </div>
                              ))
                            : deal?.primary_advisor
                              ? <>{deal.primary_advisor} (<span className="capitalize">{deal?.side ? deal.side.toLowerCase().replace(/_?broker/gi, "").trim() : "-"}</span>)</>
                              : "-"}
                        </td>
                        <td className="p-4">{deal?.acquisition_name || "-"}</td>
                        <td className="p-4">{deal?.disposition_name || "-"}</td>
                        <td className="p-4">
                          {deal?.property?.noi
                            ? "$" + deal.property.noi.toLocaleString()
                            : "-"}
                        </td>
                        <td className="p-4">
                          {deal?.contract_cap_rate || "-"}
                        </td>
                        <td className="p-4">{deal?.contract_price || "-"}</td>
                        <td className="p-4">{deal?.gci_to_company || "-"}</td>
                        <td className="p-4 whitespace-nowrap">
                          {activeToggle === 2
                            ? deal?.my_commission_format || "-"
                            : formattedTotalAdvisorCommission}
                        </td>
                        <td className="p-4">{formattedHalf}</td>
                        <td className="p-4">
                          {deal?.executed_loi_date || "-"}
                        </td>
                        <td className="p-4">{deal?.contract_date || "-"}</td>
                        <td className="p-4">
                          {deal?.expected_close_date || "-"}
                        </td>
                        <td className="p-4">{deal?.closed_date || "-"}</td>
                        <td className="p-4">{deal?.lost_date || "-"}</td>
                      </tr>

                      {/* Expanded Row Content */}
                      {expandedRow === deal.id &&
                        deal.advisors &&
                        (() => {
                          const filteredAdvisors =
                            activeToggle === 2 && userDetails?.id
                              ? deal.advisors.filter(
                                  (adv) => adv.id === userDetails.id,
                                )
                              : deal.advisors;

                          if (filteredAdvisors.length === 0) return null;

                          return (
                            <tr className="border-t-0">
                              <td
                                style={{ backgroundColor: "#f9fafb" }}
                                className="border-t-0"
                              ></td>
                              <td
                                colSpan="16"
                                style={{ backgroundColor: "#f9fafb" }}
                                className="px-4 pb-6 pt-2 border-t-0"
                              >
                                <div className="w-[600px]">
                                  <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-3">
                                    {activeToggle === 2
                                      ? "My Commission Breakdown"
                                      : "Advisor Commission Breakdown"}
                                  </h3>
                                  <div className="w-full text-left">
                                    <div className="flex border-b border-gray-200 text-gray-400 font-semibold text-[9px] uppercase tracking-wider pb-2">
                                      <div className="w-1/4">Advisor</div>
                                      <div className="w-1/4">Role</div>
                                      <div className="w-1/4">
                                        Commission % (of side)
                                      </div>
                                      <div className="w-1/4 text-center">
                                        Commission Amount
                                      </div>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                      {filteredAdvisors.map((item, idx) => (
                                        <div
                                          key={idx}
                                          className="flex text-gray-700 py-3"
                                        >
                                          <div className="w-1/4 font-semibold">
                                            {item.name}
                                          </div>
                                          <div className="w-1/4 capitalize">
                                            ({item.type
                                              ? item.type
                                                  .toLowerCase()
                                                  .replace(/_?broker/gi, "")
                                                  .trim()
                                              : "-"})
                                          </div>
                                          <div className="w-1/4">
                                            {item.commission}%
                                          </div>
                                          <div className="w-1/4 text-center font-bold">
                                            {item.commission_format}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                    </React.Fragment>
                  );
                })
              )}

                {/* Totals Row */}
                {dashboardData?.deal && dashboardData.deal.length > 0 && (
                  <tr className="font-bold text-gray-800 border-t-2 border-gray-200">
                  <td
                    style={{ backgroundColor: "#f9fafb" }}
                    className="p-4"
                  ></td>
                  <td
                    style={{ backgroundColor: "#f9fafb" }}
                    className="p-4 text-[10px] uppercase tracking-wider"
                  >
                    Totals ({dashboardData?.total || 0} Deals)
                  </td>
                  <td
                    style={{ backgroundColor: "#f9fafb" }}
                    colSpan="4"
                    className="p-4"
                  ></td>
                  <td style={{ backgroundColor: "#f9fafb" }} className="p-4">
                    {dashboardData?.totals?.total_noi || "-"}
                  </td>
                  <td style={{ backgroundColor: "#f9fafb" }} className="p-4">
                    -
                  </td>
                  <td style={{ backgroundColor: "#f9fafb" }} className="p-4">
                    -
                  </td>
                  <td style={{ backgroundColor: "#f9fafb" }} className="p-4">
                    {dashboardData?.totals?.total_gci_to_company || "-"}
                  </td>
                  <td
                    style={{ backgroundColor: "#f9fafb" }}
                    className="p-4 text-green-600"
                  >
                    {dashboardData?.totals?.total_broker_commission || "-"}
                  </td>
                  <td style={{ backgroundColor: "#f9fafb" }} className="p-4">
                    {dashboardData?.totals?.total_gci_to_company || "-"}
                  </td>
                  <td
                    style={{ backgroundColor: "#f9fafb" }}
                    colSpan="5"
                    className="p-4"
                  ></td>
                </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-1 border-t border-gray-100">
            <ContactPagination
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              onSuccess={() => {}}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer disclaimer and refresh */}
      <DataAsOfNotice message="Commissions are based on current splits and are subject to change." />

      {/* Export Modal */}
      {showModal && (
        <ExportModal
          showModal={showModal}
          selectedData={selectedData}
          exportCsvData={exportCsvData}
          from={from}
          type={exportType}
          onCallApi={onCallApi}
          advanceFilterData={advanceFilterData}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default TransactionDashboardNew;
